import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface ThemeColors {
  [key: string]: string;
}

interface ThemeContextType {
  colors: ThemeColors;
  setColors: (colors: ThemeColors) => void;
  loadingTheme: boolean;
}

const defaultColors: ThemeColors = {
  primary: '#4A90E2',
  'primary-hover': '#3B82F6',
  'primary-foreground': '#FFFFFF',
  secondary: '#10B981',
  'secondary-hover': '#059669',
  'secondary-foreground': '#FFFFFF',
  accent: '#F59E0B',
  'accent-foreground': '#FFFFFF',
  danger: '#EF4444',
  'danger-hover': '#DC2626',
  'danger-foreground': '#FFFFFF',
  success: '#10B981',
  warning: '#F59E0B',
  info: '#3B82F6',
  background: '#F4F7FA',
  foreground: '#111827',
  card: '#FFFFFF',
  'card-foreground': '#111827',
  'menu-hover-background': '#EFF6FF',
  'menu-active-background': '#DBEAFE',
  'menu-active-foreground': '#2563EB',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [colors, setColors] = useState<ThemeColors>(defaultColors);
  const [loadingTheme, setLoadingTheme] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      const { data } = await supabase
        .from('configuracoes_sistema')
        .select('valor')
        .eq('chave', 'theme_colors')
        .single();

      if (data && data.valor) {
        setColors(prev => ({ ...prev, ...data.valor as ThemeColors }));
      }
      setLoadingTheme(false);
    };
    fetchTheme();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    for (const [key, value] of Object.entries(colors)) {
      root.style.setProperty(`--color-${key}`, value);
    }
  }, [colors]);

  const value = { colors, setColors, loadingTheme };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider');
  }
  return context;
}